import { useCallback, useEffect, useRef, useState } from "react";

// Mirrors the server `enrollment_drafts` row (see shared/schemas/enrollments.ts).
export interface EnrollmentDraft {
  id: number;
  employeeId: number;
  clientId: number | null;
  periodKey: string;
  enrollmentSource: string | null;
  wizardState: Record<string, any>;
  signedKeys: string[];
  createdAt: string;
  updatedAt: string;
}

interface SaveDraftInput {
  wizardState?: Record<string, any>;
  signedKeys?: string[];
  enrollmentSource?: string | null;
}

/**
 * Server-side autosave for an in-progress enrollment, keyed by employee +
 * enrollment period. Because the draft lives on the server (not the browser),
 * a broker impersonating the employee resumes the same saved progress.
 *
 * Returns the loaded draft (once `isLoaded`), plus `saveDraft` (debounced
 * upsert), `saveNow` (immediate), and `clearDraft` (discard on submit / start
 * over). Autosave is best-effort: failures are swallowed so they never block
 * the enrollment flow.
 */
export function useEnrollmentDraft(
  employeeId?: number | null,
  periodKey?: string | null,
  options?: { debounceMs?: number },
) {
  const debounceMs = options?.debounceMs ?? 1200;
  const enabled = !!employeeId && !!periodKey;

  const [draft, setDraft] = useState<EnrollmentDraft | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<SaveDraftInput | null>(null);

  // Load the existing draft once per (employee, period).
  useEffect(() => {
    let cancelled = false;
    if (!enabled) {
      setDraft(null);
      setIsLoaded(true);
      return;
    }
    setIsLoaded(false);
    fetch(
      `/api/employees/${employeeId}/enrollment-drafts?periodKey=${encodeURIComponent(
        periodKey!,
      )}`,
      { credentials: "include" },
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled) return;
        setDraft(d ?? null);
        setIsLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setIsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, employeeId, periodKey]);

  const putDraft = useCallback(
    async (payload: SaveDraftInput) => {
      if (!enabled) return;
      try {
        const res = await fetch(
          `/api/employees/${employeeId}/enrollment-drafts`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ periodKey, ...payload }),
          },
        );
        if (res.ok) setDraft(await res.json());
      } catch {
        // best-effort autosave — never block enrollment on a save failure
      }
    },
    [enabled, employeeId, periodKey],
  );

  // Debounced save: coalesces rapid edits into a single PUT.
  const saveDraft = useCallback(
    (payload: SaveDraftInput) => {
      if (!enabled) return;
      pendingRef.current = { ...pendingRef.current, ...payload };
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        const p = pendingRef.current;
        pendingRef.current = null;
        if (p) void putDraft(p);
      }, debounceMs);
    },
    [enabled, putDraft, debounceMs],
  );

  const saveNow = useCallback(
    (payload: SaveDraftInput) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      pendingRef.current = null;
      return putDraft(payload);
    },
    [putDraft],
  );

  const clearDraft = useCallback(async () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    pendingRef.current = null;
    setDraft(null);
    if (!enabled) return;
    try {
      await fetch(
        `/api/employees/${employeeId}/enrollment-drafts?periodKey=${encodeURIComponent(
          periodKey!,
        )}`,
        { method: "DELETE", credentials: "include" },
      );
    } catch {
      // ignore — nothing actionable if discard fails
    }
  }, [enabled, employeeId, periodKey]);

  // Flush any pending debounced save on unmount.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return { draft, isLoaded, saveDraft, saveNow, clearDraft };
}
